import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, update, child } from "firebase/database";
import { IBlogCategory } from "@interfaces/iblogCategory";
import { logService } from "../services/LogService";

class BlogCategoryStore {
  categories: IBlogCategory[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchCategories = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "blogCategories"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allCategories: IBlogCategory[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.categories = allCategories.filter(category => !category.deleted);
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.categories = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
      runInAction(() => {
        this.error = "Erro ao carregar categorias.";
        this.loading = false;
      });
    }
  };

  saveCategory = async (category: IBlogCategory) => {
    const dbRef = ref(db, `blogCategories/${category.id}`);
    
    // Busca o valor anterior para verificar se é update ou create
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    const isUpdate = !!oldValue;
    
    await set(dbRef, category);
    
    // Registra o log
    const logData = isUpdate 
      ? logService.updateLogData(
          'BlogCategory',
          category.id,
          `blogCategories/${category.id}`,
          oldValue,
          category,
          `Categoria ${isUpdate ? 'atualizada' : 'criada'}: ${category.title}`
        )
      : logService.createLogData(
          'BlogCategory',
          category.id,
          `blogCategories/${category.id}`,
          category,
          `Categoria criada: ${category.title}`
        );
    
    await logService.logOperation(logData);
    
    this.fetchCategories(); // Recarrega a lista após salvar
  };

  deleteCategory = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir esta categoria?");
    if (!confirmed) {
      return;
    }

    // Busca o valor anterior para o log
    const dbRef = ref(db, `blogCategories/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `blogCategories/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (oldValue) {
      const logData = logService.deleteLogData(
        'BlogCategory',
        id,
        `blogCategories/${id}`,
        oldValue,
        `Categoria excluída: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchCategories(); // Recarrega a lista após deletar
  };

  toggleActive = async (id: string, active: boolean) => {
    const dbRef = ref(db, `blogCategories/${id}`);
    
    // Busca o valor anterior para o log
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    await update(dbRef, { active });
    
    // Registra o log
    if (oldValue) {
      const newValue = { ...oldValue, active };
      const logData = logService.toggleLogData(
        'BlogCategory',
        id,
        `blogCategories/${id}`,
        oldValue,
        newValue,
        `Categoria ${active ? 'ativada' : 'desativada'}: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchCategories();
  };
}

export const blogCategoryStore = new BlogCategoryStore();
